import { Injectable } from '@angular/core';
import {
  AgentsService,
  ClientsService,
  DistributorsService,
  ListHelperService,
  ProfileService,
  StoreService,
  CountriesService
} from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { EntityQuestion } from 'core/components';

@Injectable()
export class ClientsListService {
  userRole: any;

  agentQuestion: EntityQuestion;
  partnerQuestion: EntityQuestion;
  searchAgentsComponent: any;
  searchPartnersComponent: any;
  constructor(
    public agentsService: AgentsService,
    public distributorsService: DistributorsService,
    public clientsService: ClientsService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService,
    public countriesService: CountriesService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
    this.initFilters();
  }

  initFilters() {
    this.agentQuestion = new EntityQuestion({
      key: 'agentIds',
      entityOptions: {
        nameField: 'email',
        listParser: (agent) => this.listHelper.parseAgent(agent)
      },
      dataProvider: this.agentsService
    });
    this.partnerQuestion = new EntityQuestion({
      key: 'distributorId',
      dataProvider: this.distributorsService
    });

    this.agentQuestion.getOptions$().subscribe((options) => this.setSearchAgentsOptions(options));
    this.partnerQuestion.getOptions$().subscribe((options) => this.setSearchPartnersOptions(options));
  }

  async getDatasource(listId, options: any = {}) {
    if (options.filters) {
      if (options.filters.agentIds) {
        this.agentQuestion.preload(options.filters.agentIds);
      }
      if (options.filters.distributorId) {
        this.partnerQuestion.preload(options.filters.distributorId);
      }
    }
    const dataSource = new TableDatasource({
      sort: 'name',
      aggregates: ['credits', 'subscriptions', 'subscription', 'agents', 'ownCoursesCount'],
      populate: ['partner'],
      columns: this.getColumns(),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { agentIds, distributorId, ...$filterValues } = filters;
    const _subscriptionsAvailable = filters['subscriptions.availablePercentage'];
    const _subscriptionsEndDate = filters['subscriptions.endDate'];
    delete $filterValues['subscriptions.availablePercentage'];
    delete $filterValues['subscriptions.endDate'];
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    const $subscriptionsFilter = this.listHelper.buildFilterString(
      { _subscriptionsAvailable, _subscriptionsEndDate },
      dataSource,
      this.getCustomFilters()
    );
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      agentIds,
      distributorId,
      $filter,
      $subscriptionsFilter
    };
    return this.clientsService.fetch(paginationOptions).then((response) => response);
  }

  getColumns() {
    const columns = [
      {
        source: 'displayName',
        label: this.translate.instant('clients.header.alias'),
        sortable: true,
        style: { width: '160px' }
      },
      {
        source: 'name',
        label: this.translate.instant('clients.header.name'),
        sortable: true,
        style: { width: '160px' },
        cellRenderer: (renderer, value, client) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/clients/${client.id}`,
            value
          )}</div>`
      },
      {
        source: 'countryId',
        label: this.translate.instant('clients.header.country'),
        sortable: true,
        style: { width: '120px' },
        valueParser: (value) => this.listHelper.parseCountryName(value),
        searchRenderer: (renderer, value, callback) => {
          const options = this.countriesService.getOptions();
          options.unshift({ key: 'any', value: this.translate.instant('properties.any') });
          return renderer`
          <gl-select
          small
          .value="${value || 'any'}"
          .options="${options}"
          @onChange="${(ev) => callback(ev.detail.value !== 'any' ? ev.detail.value : null)}">
          </gl-select>
          `;
        }
      },
      {
        source: 'subscriptions.users',
        label: this.translate.instant('clients.header.plan'),
        dataType: DataTypes.boolean,
        visible: this.profileService.canExecute('clients.columns.subscriptions'),
        style: { width: '120px' },
        cellRenderer: (renderer, value) =>
          this.translate.instant(value ? 'subscriptions.users' : 'subscriptions.credits'),
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'any', value: this.translate.instant('properties.any') },
            { key: 'credits', value: this.translate.instant('subscriptions.credits') },
            { key: 'users', value: this.translate.instant('subscriptions.users') }
          ];
          return renderer`<gl-select small .options="${options}" .value="${value || 'any'}" @onChange="${(e) =>
            callback(e.detail.value !== 'any' ? e.detail.value : null)}" ></gl-select>`;
        }
      },
      {
        source: 'subscriptions.total',
        dataType: DataTypes.number,
        label: this.translate.instant('clients.header.hired'),
        allowFiltering: false,
        style: { width: '100px' }
      },
      {
        source: 'subscriptions.availablePercentage',
        label: this.translate.instant('clients.header.available'),
        sortable: true,
        style: { width: '100px' },
        dataType: DataTypes.number,
        valueParser: (value, client) => {
          const percentage = client.subscriptions.availablePercentage;
          return `${client.subscriptions.available} ${percentage > 0 ? `(${percentage}%)` : ''}`;
        },
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'any', value: this.translate.instant('properties.any') },
            { key: '20', value: '0-20%' },
            { key: '40', value: '21-40%' },
            { key: '60', value: '41-60%' },
            { key: '80', value: '61-80%' },
            { key: '100', value: '81-100%' }
          ];
          return renderer`<gl-select small .options="${options}" .value="${value || 'any'}" @onChange="${(e) =>
            callback(e.detail.value !== 'any' ? e.detail.value : null)}" ></gl-select>`;
        }
      },
      {
        source: 'subscriptions.endDate',
        label: this.translate.instant('clients.header.expirationDate'),
        sortable: true,
        style: { width: '110px' },
        dataType: DataTypes.datetime,
        valueParser: (value, client) => this.listHelper.parseDate(client.subscriptions.endDate, client.timezone)
      },
      {
        source: 'agentIds',
        label: this.translate.instant('clients.header.administrators'),
        visible: false,
        style: { width: '200px' },
        dataType: DataTypes.array,
        valueParser: (value, client) => {
          const { agents = [] } = client;
          if (agents && agents.length) {
            return agents.map((agent) => this.listHelper.parseAgent(agent));
          }
          return '';
        },
        searchRenderer: (renderer, value, callback) => {
          return renderer`
            <gl-input-tag
              .value="${value}"
              .options="${this.agentQuestion.options}"
              tagsLimit="1"
              small
              .createTags="${false}"
              @onKeyup="${(ev) => this.onSearchAgents(ev)}"
              @onChange="${(ev) => callback(ev.detail.value)}"
            >
            </gl-input-tag>
          `;
        }
      },
      {
        source: 'distributorId',
        label: this.translate.instant('clients.header.distributor'),
        visible: false,
        style: { width: '160px' },
        valueParser: (value, client) => client.partner?.name || '',
        cellRenderer: (renderer, value, group) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/distributors/${group.distributorId}`,
            value
          )}</div>`,
        searchRenderer: (renderer, value, callback) => {
          return renderer`
            <gl-input-tag
              .value="${value}"
              .options="${this.partnerQuestion.options}"
              tagsLimit="1"
              small
              .createTags="${false}"
              @onKeyup="${(ev) => this.onSearchPartners(ev)}"
              @onChange="${(ev) => callback(ev.detail.value)}"
            >
            </gl-input-tag>
          `;
        }
      },
      {
        source: 'ssoEnabled',
        dataType: DataTypes.boolean,
        label: this.translate.instant('clients.header.integration'),
        visible: false,
        style: { width: '100px' }
      },
      {
        source: 'ownCoursesCount',
        dataType: DataTypes.number,
        label: this.translate.instant('clients.header.personalizations'),
        allowFiltering: false,
        visible: false,
        style: { width: '100px' }
      }
    ];
    return columns;
  }

  onSearchAgents(ev) {
    this.searchAgentsComponent = ev.target;
    this.agentQuestion.onKeyup(ev.detail.value);
  }

  setSearchAgentsOptions(options) {
    if (this.searchAgentsComponent) {
      this.searchAgentsComponent.options = options;
    }
  }

  onSearchPartners(ev) {
    this.searchPartnersComponent = ev.target;
    this.partnerQuestion.onKeyup(ev.detail.value);
  }

  setSearchPartnersOptions(options) {
    if (this.searchPartnersComponent) {
      this.searchPartnersComponent.options = options;
    }
  }

  getCustomFilters() {
    return {
      'subscriptions.users': (filterBuilder, value) => {
        const fieldName = value === 'users' ? 'subscriptions/users' : 'subscriptions/credits';
        return filterBuilder.eq(fieldName, true);
      },
      _subscriptionsAvailable: (filterBuilder, value) => {
        const max = +value;
        const min = max === 20 ? 0 : max - 19;
        return filterBuilder.ge('subscriptions/availablePercentage', min).le('subscriptions/availablePercentage', max);
      },
      _subscriptionsEndDate: (filterBuilder, value) => {
        return filterBuilder.ge('subscriptions/endDate', value[0]).le('subscriptions/endDate', value[1]);
      }
    };
  }
}
